import React, { useRef, useState, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Box, Html } from '@react-three/drei';
import * as THREE from 'three';

const Furniture = ({
  id,
  length,
  width,
  height,
  position,
  rotation,
  color,
  name,
  isSelected,
  onSelect,
  onDeselect,
  onPositionChange,
  onRotationChange,
  checkCollision,
  roomDimensions,
  onDragStart,
  onDragEnd
}) => {
  const meshRef = useRef();
  const { camera, gl } = useThree();
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(new THREE.Vector3());
  const [mouseDownTime, setMouseDownTime] = useState(0);

  useFrame((state) => {
    if (isDragging && meshRef.current) {
      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();
      
      // 获取鼠标位置
      const rect = gl.domElement.getBoundingClientRect();
      mouse.x = state.mouse.x;
      mouse.y = state.mouse.y;
      
      // 创建拖拽平面
      const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
      const intersection = new THREE.Vector3();
      
      raycaster.setFromCamera(mouse, camera);
      raycaster.ray.intersectPlane(dragPlane, intersection);
      
      // 应用偏移量
      intersection.add(dragOffset);
      
      // 限制在房间范围内
      const halfLength = roomDimensions.length / 2 - length / 2;
      const halfWidth = roomDimensions.width / 2 - width / 2;
      
      intersection.x = Math.max(-halfLength, Math.min(halfLength, intersection.x));
      intersection.z = Math.max(-halfWidth, Math.min(halfWidth, intersection.z));
      
      // 检查碰撞
      if (!checkCollision([intersection.x, height / 2, intersection.z])) {
        onPositionChange([intersection.x, height / 2, intersection.z]);
      }
    }
  });

  const handlePointerDown = (event) => {
    event.stopPropagation();
    setMouseDownTime(Date.now());
    
    // 如果家具未选中，先选中
    if (!isSelected) {
      console.log('选中家具:', id);
      onSelect();
      return; // 选中后不开始拖拽
    }
    
    // 如果家具已选中，开始拖拽
    console.log('开始拖拽家具:', id);
    
    // 计算拖拽偏移量
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const rect = gl.domElement.getBoundingClientRect();
    
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const intersection = new THREE.Vector3();
    
    raycaster.setFromCamera(mouse, camera);
    raycaster.ray.intersectPlane(dragPlane, intersection);
    
    const offset = new THREE.Vector3();
    offset.copy(meshRef.current.position).sub(intersection);
    setDragOffset(offset);
    
    setIsDragging(true);
    onDragStart && onDragStart();
  };

  const handlePointerUp = () => {
    // 只处理拖拽结束，不自动取消选中
    setIsDragging(false);
    onDragEnd && onDragEnd();
  };

  // 添加全局鼠标事件监听
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        onDragEnd && onDragEnd();
      }
    };

    if (isDragging) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('mouseleave', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('mouseleave', handleGlobalMouseUp);
    };
  }, [isDragging, onDragEnd]);

  const handleDoubleClick = (event) => {
    event.stopPropagation();
    // 双击旋转90度
    const newRotation = [...rotation];
    newRotation[1] += Math.PI / 2;
    onRotationChange(newRotation);
  };

  return (
    <group>
      <Box
        ref={meshRef}
        args={[length, height, width]}
        position={position}
        rotation={rotation}
        castShadow
        receiveShadow
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onDoubleClick={handleDoubleClick}
      >
        <meshStandardMaterial 
          color={isSelected ? "#FFD700" : color}
          transparent
          opacity={isSelected ? 0.8 : 1}
        />
      </Box>
      
      {isSelected && (
        <Html position={[position[0], position[1] + height / 2 + 0.5, position[2]]}>
          <div style={{
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none'
          }}>
            {name || `物品 ${id}`}
          </div>
        </Html>
      )}
    </group>
  );
};

export default Furniture; 